import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, Users, Mail, Settings, LogOut, Music, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const Logo = () => (
  <Link to="/admin/events" className="flex items-center gap-2 px-4 py-6">
    <img 
      src="/exit88-logo.png" 
      alt="EXIT88" 
      className="h-10 w-auto object-contain"
    />
    <span className="text-primary font-bold text-xl">LIVE</span>
  </Link>
);

const NavItem = ({ to, icon: Icon, label, active, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={cn(
      "flex items-center gap-3 px-4 py-3 rounded-lg transition-all mx-2",
      active 
        ? "bg-primary text-primary-foreground" 
        : "text-muted-foreground hover:text-white hover:bg-secondary"
    )}
  >
    <Icon className="w-5 h-5" />
    <span>{label}</span>
  </Link>
);

// Desktop Sidebar (hidden on mobile)
export function AdminSidebar() {
  const location = useLocation();
  const path = location.pathname;

  const isEventsActive = path === '/admin' || path === '/admin/events' || (path.startsWith('/admin/events') && !path.includes('/artists'));
  const isArtistsActive = path.startsWith('/admin/artists');
  const isCustomersActive = path.startsWith('/admin/customers');
  const isCampaignsActive = path.startsWith('/admin/campaigns');
  const isSettingsActive = path.startsWith('/admin/settings');

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border flex-col z-50 hidden lg:flex">
      <Logo />
      
      <nav className="flex-1 py-4 space-y-1">
        <NavItem 
          to="/admin/events" 
          icon={Calendar} 
          label="Events" 
          active={isEventsActive} 
        />
        <NavItem 
          to="/admin/artists" 
          icon={Music} 
          label="Artists" 
          active={isArtistsActive} 
        />
        <NavItem 
          to="/admin/customers" 
          icon={Users} 
          label="Customers" 
          active={isCustomersActive} 
        />
        <NavItem 
          to="/admin/campaigns" 
          icon={Mail} 
          label="Email Center" 
          active={isCampaignsActive} 
        />
      </nav>

      <div className="border-t border-border py-4 space-y-1">
        <NavItem to="/admin/settings" icon={Settings} label="Settings" active={isSettingsActive} />
        <NavItem to="/" icon={LogOut} label="Logout" active={false} />
      </div>
    </aside>
  );
}

// Mobile Header with hamburger menu
export function AdminMobileHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const path = location.pathname;

  const isEventsActive = path === '/admin' || path === '/admin/events' || (path.startsWith('/admin/events') && !path.includes('/artists'));
  const isArtistsActive = path.startsWith('/admin/artists');
  const isCustomersActive = path.startsWith('/admin/customers');
  const isCampaignsActive = path.startsWith('/admin/campaigns');
  const isSettingsActive = path.startsWith('/admin/settings');

  return (
    <>
      {/* Mobile Header Bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 z-50">
        <Link to="/admin/events" className="flex items-center gap-2">
          <img 
            src="/exit88-logo.png" 
            alt="EXIT88" 
            className="h-8 w-auto object-contain"
          />
          <span className="text-primary font-bold text-lg">LIVE</span>
        </Link>
        <button 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-2 hover:bg-secondary rounded-lg"
          data-testid="mobile-menu-btn"
        >
          {isMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/60" onClick={() => setIsMenuOpen(false)} />
          <nav className="absolute top-16 left-0 right-0 bg-card border-b border-border py-4 space-y-1">
            <NavItem 
              to="/admin/events" 
              icon={Calendar} 
              label="Events" 
              active={isEventsActive}
              onClick={() => setIsMenuOpen(false)}
            />
            <NavItem 
              to="/admin/artists" 
              icon={Music} 
              label="Artists" 
              active={isArtistsActive}
              onClick={() => setIsMenuOpen(false)}
            />
            <NavItem 
              to="/admin/customers" 
              icon={Users} 
              label="Customers" 
              active={isCustomersActive}
              onClick={() => setIsMenuOpen(false)}
            />
            <NavItem 
              to="/admin/campaigns" 
              icon={Mail} 
              label="Email Center" 
              active={isCampaignsActive}
              onClick={() => setIsMenuOpen(false)}
            />
            <div className="border-t border-border mt-4 pt-4">
              <NavItem to="/admin/settings" icon={Settings} label="Settings" active={isSettingsActive} onClick={() => setIsMenuOpen(false)} />
              <NavItem to="/" icon={LogOut} label="Logout" active={false} onClick={() => setIsMenuOpen(false)} />
            </div>
          </nav>
        </div>
      )}
    </>
  );
}

export default AdminSidebar;
